import { createFileRoute, useLocation } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Search, Clipboard, Map, Code, TrendingUp, HelpCircle } from "lucide-react"
import { SEO } from "@/components/SEO"
import { StructuredData } from "@/components/StructuredData"
import { useTheme } from "@/providers/ThemeProvider"
import { processPage, siteMetadata } from "@/content"

export const Route = createFileRoute("/process")({
  component: ProcessPage,
})

const iconMap = {
  search: Search,
  clipboard: Clipboard,
  map: Map,
  code: Code,
  "trending-up": TrendingUp,
}

const floatAnimations = [
  "animate-float-slow",
  "animate-float-medium",
  "animate-float-fast",
  "animate-float-slow",
  "animate-float-medium",
]

function ProcessPage() {
  const location = useLocation()
  const { theme } = useTheme()
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

  return (
    <>
      <SEO metadata={processPage.seo} />
      <StructuredData type="process" pageTitle="Our Process" pageUrl={`${siteMetadata.url}${location.pathname}`} />
      <div className="relative py-20 px-6 overflow-hidden">
        {/* Animated Neural Network Background */}
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: isDark ? 0.25 : 0.1 }}>
          <svg width="100%" height="100%" viewBox="0 0 1000 1200" className="absolute inset-0">
            <defs>
              <radialGradient id="nodeGlowProcess" cx="50%" cy="30%" r="70%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="1" />
                <stop offset="50%" stopColor="rgb(147, 51, 234)" stopOpacity="0.8" />
                <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
              </radialGradient>
              <linearGradient id="lineGradientProcess" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgb(147, 51, 234)" stopOpacity="0.4" />
              </linearGradient>
            </defs>

            {/* Subtle network pattern */}
            <g opacity="0.8">
              <g stroke="url(#lineGradientProcess)" strokeWidth="1" fill="none">
                <line x1="150" y1="100" x2="300" y2="150" />
                <line x1="300" y1="150" x2="450" y2="120" />
                <line x1="700" y1="250" x2="850" y2="300" />
                <line x1="100" y1="400" x2="250" y2="450" />
                <line x1="250" y1="450" x2="400" y2="420" />
                <line x1="750" y1="550" x2="900" y2="600" />
                <line x1="150" y1="750" x2="300" y2="800" />
                <line x1="700" y1="900" x2="850" y2="950" />
              </g>
              <g fill="url(#nodeGlowProcess)">
                <circle cx="150" cy="100" r="3" className="animate-networkPulse" />
                <circle cx="300" cy="150" r="4" />
                <circle cx="450" cy="120" r="3" />
                <circle cx="700" cy="250" r="4" className="animate-networkPulse" />
                <circle cx="850" cy="300" r="3" />
                <circle cx="100" cy="400" r="3" />
                <circle cx="250" cy="450" r="4" className="animate-networkPulse" />
                <circle cx="400" cy="420" r="3" />
                <circle cx="750" cy="550" r="4" />
                <circle cx="900" cy="600" r="3" className="animate-networkPulse" />
                <circle cx="150" cy="750" r="3" />
                <circle cx="300" cy="800" r="4" />
                <circle cx="700" cy="900" r="3" className="animate-networkPulse" />
                <circle cx="850" cy="950" r="4" />
              </g>
            </g>
          </svg>
        </div>

        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-purple-500/[0.02] pointer-events-none"></div>

        <div className="relative max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{processPage.header.title}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{processPage.header.subtitle}</p>
          </div>

          {/* Questions Section */}
          <Card className="mb-16 bg-gradient-to-br from-primary/5 to-purple-500/10 border-primary/20 animate-gradient-shift overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 animate-gradient-shift"></div>
            <CardContent className="relative p-8 md:p-12">
              <h2 className="text-2xl font-semibold mb-6 text-center">{processPage.questions.title}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {processPage.questions.items.map((question, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <HelpCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0 animate-networkPulse" />
                    <p className="text-lg font-medium">{question}</p>
                  </div>
                ))}
              </div>
              <p className="text-center text-lg text-muted-foreground font-medium">{processPage.questions.intro}</p>
            </CardContent>
          </Card>

          {/* Intro */}
          <div className="mb-16">
            <h2 className="text-3xl font-semibold mb-6 text-center">{processPage.intro.title}</h2>
            <div className="max-w-4xl mx-auto space-y-4 text-center text-muted-foreground">
              {processPage.intro.content.map((paragraph, index) => (
                <p key={index} className="text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Process Phases */}
          <div className="mb-16 relative">
            {/* Vertical Flow Connector */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/30 via-purple-500/30 to-primary/30 -translate-x-1/2 hidden lg:block"></div>

            <div className="space-y-6">
              {processPage.phases.map((phase, index) => {
                const PhaseIcon = iconMap[phase.icon as keyof typeof iconMap]
                const floatClass = floatAnimations[index]
                return (
                  <Card
                    key={index}
                    className="overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group border-border/50 hover:border-primary/30 relative"
                  >
                    {/* Flow connector dot */}
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-primary rounded-full border-4 border-background hidden lg:block z-10 group-hover:scale-125 transition-transform"></div>

                    <CardContent className="p-0">
                      <div className="grid grid-cols-1 lg:grid-cols-12">
                        {/* Phase Number & Icon */}
                        <div className="lg:col-span-2 bg-gradient-to-br from-primary/5 to-purple-500/5 flex flex-col items-center justify-center p-8 border-b lg:border-b-0 lg:border-r border-border/50 relative overflow-hidden">
                          {/* Gradient accent */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                          <div className="relative z-10 text-5xl font-bold bg-gradient-to-br from-primary to-purple-600 bg-clip-text text-transparent mb-4">
                            {phase.number}
                          </div>
                          <PhaseIcon className={`relative z-10 h-12 w-12 text-primary ${floatClass}`} />
                        </div>

                        {/* Phase Content */}
                        <div className="lg:col-span-10 p-8 relative">
                          <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-200">
                            {phase.title}
                          </h3>
                          <p className="text-lg text-primary mb-4">{phase.subtitle}</p>
                          <p className="text-muted-foreground leading-relaxed">{phase.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>

          {/* Why Our Process Works */}
          <div className="mb-16">
            <h2 className="text-3xl font-semibold mb-8 text-center">{processPage.why.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {processPage.why.reasons.map((reason, index) => (
                <Card
                  key={index}
                  className="group hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-border/50 hover:border-primary/30"
                >
                  <CardContent className="p-6 relative overflow-hidden">
                    {/* Hover gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors duration-200">
                      {reason.title}
                    </h3>
                    <p className="text-muted-foreground">{reason.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Card className="bg-gradient-to-br from-primary/5 to-purple-500/10 border-primary/20 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5 animate-gradient-shift"></div>
              <CardContent className="relative p-12">
                <h2 className="text-3xl font-semibold mb-4">{processPage.cta.title}</h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">{processPage.cta.subtitle}</p>
                <Button
                  asChild
                  size="lg"
                  className="shadow-lg hover:shadow-xl transition-shadow duration-200 hover:scale-105 transform"
                >
                  <a href={processPage.cta.buttonHref} target="_blank" rel="noopener noreferrer">
                    {processPage.cta.buttonText}
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
